import axios from 'axios';
import { API } from '../config';

/* Convert a base64url VAPID key to the Uint8Array the PushManager expects. */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/* Register the service worker (also enables PWA install). Returns the registration. */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    return registration;
  } catch (err) {
    console.error('Service worker registration failed:', err);
    return null;
  }
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

/* Ask permission, subscribe, and register the subscription with the backend. */
export async function enablePush() {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported on this device/browser.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }

  const { data } = await axios.get(`${API}/push/vapid-public-key`);
  if (!data.publicKey) {
    throw new Error('Push is not configured on the server.');
  }

  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }

  await axios.post(`${API}/push/subscribe`, { subscription: subscription.toJSON() });
  return true;
}

/* Unsubscribe this device and tell the backend to stop sending. */
export async function disablePush() {
  try {
    const subscription = await getCurrentSubscription();
    if (subscription) {
      await axios.post(`${API}/push/unsubscribe`, { subscription: subscription.toJSON() });
      await subscription.unsubscribe();
    }
  } catch (err) {
    console.error('Failed to unsubscribe:', err);
  }
  // Ensure server-side flag is off even if no local subscription existed
  try {
    await axios.put(`${API}/push/settings`, { pushEnabled: false });
  } catch (e) {
    /* ignore */
  }
  return true;
}

export async function sendTestPush() {
  const { data } = await axios.post(`${API}/push/test`);
  return data;
}
